import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import MainLayout from "../layouts/MainLayout";
import DashboardLayout from "../layouts/DashboardLayout";
import ProtectedLayout from "../layouts/ProtectedLayout";
import LoginPage from "../pages/auth/LoginPage.jsx";
import LandingPageNew from "../pages/landing/LandingPageNew.jsx";
import LandingGuidePage from "../pages/landing/LandingGuidePage.jsx";
import PublicLayout from "../layouts/AuthLayout.jsx";
import { AuthContextProvider } from "../context/authContext.js";
import { SidebarProvider } from "../context/sidebarContext.js";
import { ContactsProvider } from "../context/contactsContext.js";
import { CompanyProvider } from "../context/companyContext.js";
import { WebSocketProvider } from "../context/websocketContext.js";
import { UISettingsProvider } from "../context/uiSettingsContext.js";
import { WabaProvider } from "../context/wabaContext.js";
import { UserContextProvider, useUserContext, hasPageAccess } from "../context/userContext.js";
import UserProfile from "../pages/user/UserProfile.jsx";
import SignupPage from "../pages/auth/SignupPage.jsx";
import WhatsAppRegistration from "../pages/whatsappRegistraton/index.jsx";
import CustomerDashboardRoute from "./CustomerDashboardRoute";
import SuperAdminDashboard from "../pages/dashboard/components/pages/adminpages/SuperAdminDashboard.jsx";
import SolutionPartnerAdminDashboard from "../pages/dashboard/components/pages/adminpages/SolutionPartnerAdminDashboard.jsx";
import ClientAdminPage from "../pages/dashboard/components/pages/clientadmin/ClientAdminPage.jsx";
import PendingCustomersPage from "../pages/dashboard/components/pages/adminpages/PendingCustomersPage.jsx";
import ActiveCustomersPage from "../pages/dashboard/components/pages/adminpages/ActiveCustomersPage.jsx";
import PricingPage from "../pages/landing/PricingPage.jsx";
import PrivacyPolicy from "../pages/landing/PrivacyPolicy.jsx";
import TermsOfService from "../pages/landing/TermsOfService.jsx";
import RefundPolicy from "../pages/landing/RefundPolicy.jsx";
// Import dashboard route components
import InboxPage from "../pages/dashboard/components/pages/InboxPage";
import ContactsRoute from "./ContactsRoute";
import TemplatesRoute from "./TemplatesRoute";
import BroadcastRoute from "./BroadcastRoute";
import NewTemplateBroadcastRoute from "./NewTemplateBroadcastRoute";
import AnalyticsRoute from "./AnalyticsRoute";
import FlowResponsesRoute from "./FlowResponsesRoute";
import OptedOutRoute from "./OptedOutRoute";
import TransactionalMessagesRoute from "./TransactionalMessagesRoute";
import TransactionalMessageDetailRoute from "./TransactionalMessageDetailRoute";
import WebhooksRoute from "./WebhooksRoute";
import WebhookDeliveryLogRoute from "./WebhookDeliveryLogRoute";
import AudienceSourcesRoute from "./AudienceSourcesRoute";
import BecomeSolutionPartner from "../pages/dashboard/BecomeSolutionPartner";
import { ChannelModeProvider } from "../context/channelModeContext";
import { ToastProvider } from "../pages/email/shared/ToastProvider";
import { OnboardingProvider } from "../context/onboardingContext";
// EmailConfig removed — SES is platform-managed, no user config needed
import EmailRoute from "./EmailRoute";
import AIChatbotRoute from "./AIChatbotRoute";
import SuperAdminAIConsole from "../pages/aiChatbot/SuperAdminAIConsole";
import BotFlowRoute from "./BotFlowRoute";
import ApiKeysRoute from "./ApiKeysRoute";
import ApiDocsRoute from "./ApiDocsRoute";import WalletRoute from './WalletRoute';import ApiDocsPublicPage from "../pages/landing/ApiDocsPublicPage";
import ProductGuide from "../pages/dashboard/ProductGuide";
import ProfileSettings from "../pages/dashboard/ProfileSettings";
import { ProfileProvider } from '../context/profileContext';
import { WalletProvider } from '../context/walletContext';

/**
 * PageGuard - Checks if the current user has access to a specific page.
 * If userCtx has no pages array (super admin / top-level), access is always granted.
 * Otherwise, the page key must be in the user's pages array.
 */
const PageGuard = ({ pageKey, children }) => {
  const { userCtx } = useUserContext();
  // If context hasn't loaded yet or user has no pages restriction, allow through
  if (!userCtx || !userCtx.pages) return children;
  // Support array of pageKeys — allow if user has access to ANY of them
  if (Array.isArray(pageKey)) {
    if (pageKey.some(k => hasPageAccess(userCtx, k))) return children;
  } else {
    if (hasPageAccess(userCtx, pageKey)) return children;
  }
  // Redirect to dashboard home if page not allowed
  return <Navigate to="/dashboard" replace />;
};

const AppRoutes = () => (
  <CompanyProvider>
    <AuthContextProvider>
      <UserContextProvider>
      <WebSocketProvider>
        <Router>
        <Routes>
         <Route element={<PublicLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        </Route>
        {/* Landing page uses its own Navbar and Footer */}
        <Route path="/" element={<LandingPageNew />} />
        <Route path="/guide" element={<LandingGuidePage />} />
        <Route path="/api-docs" element={<ApiDocsPublicPage />} />
        <Route element={<PublicLayout />}>
         <Route path="/pricing" element={<PricingPage />} />
         <Route path="/privacy-policy" element={<PrivacyPolicy />} />
         <Route path="/terms-of-service" element={<TermsOfService />} />
         <Route path="/refund-policy" element={<RefundPolicy />} />
        </Route>
        
        {/* Protected Routes - Requires Authentication */}
        <Route element={<ProtectedLayout />}>
          <Route
            element={
              <SidebarProvider>
                <MainLayout />
              </SidebarProvider>
            }
          >
            <Route path="/whatsappregistration" element={<WhatsAppRegistration />} />
            <Route path="/myprofile" element={<UserProfile />} />
          </Route>
          
          
          {/* Customer Dashboard Routes with DashboardLayout */}
          <Route
            element={
              <ChannelModeProvider>
              <ToastProvider>
              <WabaProvider>
                <OnboardingProvider>
                <UISettingsProvider>
                  <WalletProvider>
                  <ContactsProvider>
                    <SidebarProvider>
                      <ProfileProvider>
                        <DashboardLayout />
                      </ProfileProvider>
                    </SidebarProvider>
                  </ContactsProvider>
                  </WalletProvider>
                </UISettingsProvider>
                </OnboardingProvider>
              </WabaProvider>
              </ToastProvider>
              </ChannelModeProvider>
            }
          >
            <Route path="/dashboard" element={<CustomerDashboardRoute />} />
            <Route path="/dashboard/customer" element={<CustomerDashboardRoute />} />
            <Route path="/dashboard/profile" element={<ProfileSettings />} />
            <Route path="/dashboard/admin" element={<PageGuard pageKey={['admin','platform_plans','topup_plans','payment_management']}><SuperAdminDashboard /></PageGuard>} />
            <Route path="/dashboard/solution-partner-admin" element={<SolutionPartnerAdminDashboard />} />
            <Route path="/dashboard/admin/pending-customers" element={<PageGuard pageKey="admin"><PendingCustomersPage /></PageGuard>} />
            <Route path="/dashboard/admin/active-customers" element={<PageGuard pageKey="admin"><ActiveCustomersPage /></PageGuard>} />
            <Route path="/dashboard/contacts" element={<PageGuard pageKey="contacts"><ContactsRoute /></PageGuard>} />
            <Route path="/dashboard/templates/*" element={<PageGuard pageKey="templates"><TemplatesRoute /></PageGuard>} />
            <Route path="/dashboard/broadcast/*" element={<PageGuard pageKey="campaigns"><BroadcastRoute /></PageGuard>} />
            <Route path="/dashboard/opted-out" element={<PageGuard pageKey="campaigns"><OptedOutRoute /></PageGuard>} />
            <Route path="/dashboard/new-template-broadcast" element={<PageGuard pageKey="campaigns"><NewTemplateBroadcastRoute /></PageGuard>} />
            <Route path="/dashboard/analytics" element={<PageGuard pageKey="dashboard"><AnalyticsRoute /></PageGuard>} />
            {/* Phase 2: Developer Tools — BYO + Transactional + Webhooks */}
            <Route path="/dashboard/transactional-messages" element={<PageGuard pageKey="api_keys"><TransactionalMessagesRoute /></PageGuard>} />
            <Route path="/dashboard/transactional-messages/:messageId" element={<PageGuard pageKey="api_keys"><TransactionalMessageDetailRoute /></PageGuard>} />
            <Route path="/dashboard/webhooks" element={<PageGuard pageKey="api_keys"><WebhooksRoute /></PageGuard>} />
            <Route path="/dashboard/webhooks/:id/deliveries" element={<PageGuard pageKey="api_keys"><WebhookDeliveryLogRoute /></PageGuard>} />
            <Route path="/dashboard/audience-sources" element={<PageGuard pageKey="api_keys"><AudienceSourcesRoute /></PageGuard>} />
            <Route path="/dashboard/external-inbox" element={<Navigate to="/dashboard/inbox" replace />} />
            <Route path="/dashboard/become-solution-partner" element={<BecomeSolutionPartner />} />
            <Route path="/dashboard/inbox" element={<PageGuard pageKey="inbox"><InboxPage /></PageGuard>} />
            <Route path="/dashboard/flow-responses" element={<PageGuard pageKey="inbox"><FlowResponsesRoute /></PageGuard>} />
            <Route path="/dashboard/email/*" element={<PageGuard pageKey={['email_dashboard','email_campaigns','email_templates','email_identity','email_preferences','email_settings','email_sender']}><EmailRoute /></PageGuard>} />
            <Route path="/dashboard/ai-chatbot/*" element={<PageGuard pageKey={['ai_chatbot','knowledge_base']}><AIChatbotRoute /></PageGuard>} />
            <Route path="/dashboard/admin/ai-console" element={<PageGuard pageKey="admin"><SuperAdminAIConsole /></PageGuard>} />
            <Route path="/dashboard/bot-flow/*" element={<PageGuard pageKey="bot_flows"><BotFlowRoute /></PageGuard>} />
            <Route path="/dashboard/client-admin" element={<PageGuard pageKey="admin"><ClientAdminPage /></PageGuard>} />
            <Route path="/dashboard/pricing" element={<PricingPage />} />
            <Route path="/dashboard/guide" element={<ProductGuide />} />
            <Route path="/dashboard/api-keys" element={<PageGuard pageKey="api_keys"><ApiKeysRoute /></PageGuard>} />
            <Route path="/dashboard/api-docs" element={<PageGuard pageKey="api_keys"><ApiDocsRoute /></PageGuard>} />
            <Route path="/dashboard/wallet" element={<PageGuard pageKey="wallet"><WalletRoute /></PageGuard>} />
          </Route>
        </Route>
        
        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Router>
      </WebSocketProvider>
      </UserContextProvider>
    </AuthContextProvider>
  </CompanyProvider>
);

export default AppRoutes;
